/**
 * The Phemex referral link and the rule for when the banner is allowed on screen.
 *
 * The rule lives here rather than inside the component because every way it fails is
 * silent: a banner that never appears looks the same as a banner nobody dismissed, and a
 * banner shown to someone already trading looks like spam rather than a bug.
 */

/**
 * The one place the referral link lives.
 *
 * Every registration through this URL is credited to us, so it must never be swapped for a
 * plain phemex.com link — a signup that loses the referral is worth less to us than no
 * banner at all. If it is ever emptied, the banner renders nothing anywhere rather than
 * offering a "Start Now" button that opens a dead tab.
 */
export const PHEMEX_SIGNUP_URL = "https://phemex.com/affiliate/FFTTG9";

/**
 * What the link is offering. Phemex's own terms are the source of truth for these.
 * Keys in the `copy` namespace (locales/en/copy.json); render with t().
 */
export const PHEMEX_SELLING_POINTS = [
  "referral.point.noKyc",
  "referral.point.noGeo",
  "referral.point.worldwide",
] as const;

export interface ReferralBannerState {
  /** The referral URL as configured. Empty disables the banner everywhere. */
  url: string;
  /** The viewer closed it before. */
  dismissed: boolean;
  /** Whether this account has a broker connected. `null` means we do not know yet. */
  hasConnection: boolean | null;
}

/**
 * The banner is a standing reminder, shown until it is dismissed.
 *
 * It was gated on having no broker connected, on the reasoning that someone already trading
 * has no use for a signup link. That was wrong twice over: Guillaume could not see his own
 * banner (2026-09-12), and the link is worth passing on by people who are already customers
 * -- referring someone else is the whole point of a referral.
 *
 * `hasConnection` is therefore no longer consulted. Dismissal is the only thing that hides
 * it, which is the control a reminder should have.
 */
export function shouldShowReferralBanner(state: ReferralBannerState): boolean {
  if (!state.url) return false;
  return !state.dismissed;
}

/**
 * The inline variant sits where someone is about to paste API keys. It is not gated on
 * having a connection: that is the moment a missing account actually blocks them, and
 * someone adding a second key still reads it as context rather than as an ad.
 */
export function shouldShowReferralInline(url: string): boolean {
  return Boolean(url);
}

/**
 * Every partner exchange with its own sign-up link and what it is good for, shown
 * side by side and in no order of preference (Guillaume's /preview review #30,
 * 2026-10-24: no "No Phemex account yet?" favouring one partner). KuCoin's is
 * CopyFi's referral link (Guillaume, 10-24).
 */
export const BROKER_SIGNUPS = [
  { broker: 'Phemex', url: PHEMEX_SIGNUP_URL, points: ['referral.point.noKyc', 'referral.point.noGeo'] },
  { broker: 'KuCoin', url: 'https://www.kucoin.com/r/broker/CXEEQ4XP', points: ['referral.point.moreAssets'] },
] as const;
